import { reduxForm, formValueSelector, isInvalid, SubmissionError, change } from 'redux-form'
import { Fade, Textarea, Select, Input, Button } from '../../components/common'
import { CommentValidation } from '../../components/common/forms/Validations'
import {
  PROCESS_REQUEST_FORM,
  PROCESS_REQUEST_STATUS_FIELD,
  PROCESS_REQUEST_COMMENTS_FIELD,
  PROCESS_REQUEST_HAND_SHIPMENT_FIELD,
  PROCESS_REQUEST_STATUS_OPTIONS,
  STATUS_DENIED,
  STATUS_NEEDS_INFO,
  STATUS_APPROVED,
  APPROVAL_REQUEST_CONFIG,
  HANDSHIPMENT_ALLOWED,
  ARTYPE_NONSTOCKEDSSD,
} from '../../constants/ApprovalRequestConstants'
import { openModal } from '../../actions/app'
import {
  stopProcessingApprovalRequest,
  submitApprovalRequest,
} from '../../actions/approvalRequests'
import {
  selectSelectedApprovalRequest,
  getApprovalRequestCloseFocusElem,
  selectSpecificApprovalRequestType,
} from '../../selectors/approvalRequests'

const selector = formValueSelector(PROCESS_REQUEST_FORM)

const mapStateToProps = state => {
  const arType = selectSpecificApprovalRequestType(state)
  const selectedStatus = selector(state, PROCESS_REQUEST_STATUS_FIELD)
  const handShipmentChecked = selector(state, PROCESS_REQUEST_HAND_SHIPMENT_FIELD)
  const handShipmentEnabled =
    HANDSHIPMENT_ALLOWED.includes(arType) && selectedStatus === STATUS_APPROVED
  const { submitting } = state.processApprovalRequest
  return {
    closeFocusElem: getApprovalRequestCloseFocusElem(state),
    arType,
    initialValues: { [PROCESS_REQUEST_STATUS_FIELD]: '', [PROCESS_REQUEST_COMMENTS_FIELD]: '' },
    invalid: isInvalid(PROCESS_REQUEST_FORM)(state),
    submitting,
    selectedApprovalRequest: selectSelectedApprovalRequest(state),
    handShipmentChecked: handShipmentEnabled ? handShipmentChecked : false,
    handShipmentEnabled,
    commentRequired: selectedStatus === STATUS_DENIED || selectedStatus === STATUS_NEEDS_INFO,
  }
}

const formStyles = { width: '100%', padding: '15px', backgroundColor: 'rgb(250, 250, 250)' }

const queryControlList =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const CloseProcessRequestForm = connect(null, { stopProcessingApprovalRequest })(
  ({ stopProcessingApprovalRequest }) => (
    <button
      type="button"
      className="btn-link"
      style={{ float: 'right', fontSize: '1.5em' }}
      onClick={() => stopProcessingApprovalRequest()}
      aria-label="Close process approval dialog"
    >
      ×
    </button>
  )
)

class ProcessApprovalRequestForm extends Component {
  constructor(props) {
    super(props)
    this.processApprovalRequestFormRef = createRef()
    this.processApprovalRequestButtonRef = createRef()
    this.state = { firstFocusable: null, lastFocusable: null }
  }

  componentDidMount() {
    // Collect list of all tabable elements within the Detail Panel
    const focusable = this.processApprovalRequestFormRef.current.querySelectorAll(queryControlList)
    this.setState({ firstFocusable: focusable[0], lastFocusable: focusable[focusable.length - 1] })
    // Set focus to containing element
    this.processApprovalRequestFormRef.current.focus()
  }

  componentDidUpdate(prevProps) {
    const { invalid, submitting, handShipmentChecked, handShipmentEnabled, change } = this.props

    if (prevProps.invalid !== invalid || prevProps.submitting !== submitting) {
      // The submit button has either been disabled or enabled, so update the list of focusable elements
      const focusable =
        this.processApprovalRequestFormRef.current.querySelectorAll(queryControlList)
      this.setState({
        firstFocusable: focusable[0],
        lastFocusable: focusable[focusable.length - 1],
      })
    }

    if (prevProps.handShipmentChecked && !handShipmentEnabled) {
      change(PROCESS_REQUEST_HAND_SHIPMENT_FIELD, false)
    }
  }

  componentWillUnmount() {
    const { closeFocusElem } = this.props

    if (closeFocusElem) {
      closeFocusElem.focus()
    }
  }

  handleKeyPress(event) {
    const { firstFocusable, lastFocusable } = this.state

    // console.log(`Key Pressed: ${event.key}, Shift Pressed: ${event.shiftKey} on element: ${document.activeElement}`)

    if (event.key !== 'Tab') return

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        // console.log('Set focus to LAST')
        lastFocusable.focus()
        event.preventDefault()
      }
    } else if (document.activeElement === lastFocusable) {
      // console.log('Set focus to FIRST')
      firstFocusable.focus()
      event.preventDefault()
    }
  }

  render() {
    const {
      handleSubmit,
      invalid,
      submitting,
      stopProcessingApprovalRequest,
      commentRequired,
      handShipmentEnabled,
      handShipmentChecked,
      arType,
      selectedApprovalRequest: { etsApprovalStatus, reprocessApprovedInd = false },
      error,
      openModal,
    } = this.props

    const isNonStockedReProcess =
      arType === ARTYPE_NONSTOCKEDSSD &&
      (etsApprovalStatus === STATUS_APPROVED || etsApprovalStatus === STATUS_NEEDS_INFO)

    // Non-stocked re-process can happen in both Approved and Needs Info status
    const processRequestOptions = isNonStockedReProcess
      ? etsApprovalStatus === STATUS_APPROVED
        ? PROCESS_REQUEST_STATUS_OPTIONS.filter(opt => opt.value === STATUS_NEEDS_INFO)
        : reprocessApprovedInd
        ? PROCESS_REQUEST_STATUS_OPTIONS.filter(opt => opt.value === STATUS_APPROVED)
        : PROCESS_REQUEST_STATUS_OPTIONS.filter(
            opt => opt.value === STATUS_APPROVED || opt.value === STATUS_DENIED
          )
      : PROCESS_REQUEST_STATUS_OPTIONS

    return (
      <Fade>
        <div
          style={formStyles}
          ref={this.processApprovalRequestFormRef}
          tabIndex="0"
          role="dialog"
          onKeyDown={event => this.handleKeyPress(event)}
        >
          <CloseProcessRequestForm />
          <h4>Process Request</h4>
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="cb-error-msg" role="alert">
                {error}
              </div>
            )}
            <Select
              name={PROCESS_REQUEST_STATUS_FIELD}
              options={processRequestOptions}
              showEmptyOption={true}
              blankLabel="status"
              isRequired={true}
              label="Status"
            />
            <Textarea
              name={PROCESS_REQUEST_COMMENTS_FIELD}
              isRequired={commentRequired}
              label={`Comments${commentRequired ? ' (required)' : ' (optional)'}`}
              rows="3"
              maxlength="500"
              validate={[CommentValidation]}
            />
            {handShipmentEnabled ? (
              <div className="form-group">
                <Input
                  type="checkbox"
                  name={PROCESS_REQUEST_HAND_SHIPMENT_FIELD}
                  label="Hand Shipment"
                  checked={handShipmentChecked}
                />
              </div>
            ) : null}
            <Button
              style={{ marginTop: '0', backgroundColor: 'rgb(255, 255, 255)' }}
              onClick={() => stopProcessingApprovalRequest()}
            >
              Cancel
            </Button>
            <Button
              reference={this.processApprovalRequestButtonRef}
              isPrimary={true}
              style={{ marginTop: '0', marginLeft: '25px' }}
              isDisabled={invalid || submitting}
              onClick={() =>
                openModal('ConfirmProcessApprovalRequestModal', {
                  isReProcess: isNonStockedReProcess,
                  modalCloseFocusElem: this.processApprovalRequestButtonRef.current,
                })
              }
              busy={submitting}
            >
              Submit
            </Button>
          </form>
        </div>
      </Fade>
    )
  }
}

export default connect(mapStateToProps, {
  stopProcessingApprovalRequest,
  submitApprovalRequest,
  change,
  openModal,
})(
  reduxForm({
    form: PROCESS_REQUEST_FORM,
    onSubmit: (values, dispatch, props) => {
      const submitValues = {
        [APPROVAL_REQUEST_CONFIG[props.arType].idField]:
          props.selectedApprovalRequest[APPROVAL_REQUEST_CONFIG[props.arType].idField],
        comments: values[PROCESS_REQUEST_COMMENTS_FIELD],
      }
      if (values[PROCESS_REQUEST_HAND_SHIPMENT_FIELD] !== undefined) {
        submitValues[PROCESS_REQUEST_HAND_SHIPMENT_FIELD] =
          values[PROCESS_REQUEST_HAND_SHIPMENT_FIELD]
      }
      if (APPROVAL_REQUEST_CONFIG[props.arType].processAdditionalFields.length > 0) {
        APPROVAL_REQUEST_CONFIG[props.arType].processAdditionalFields.forEach(
          field => (submitValues[field] = props.selectedApprovalRequest[field])
        )
      }

      switch (values[PROCESS_REQUEST_STATUS_FIELD]) {
        case STATUS_APPROVED:
        case STATUS_NEEDS_INFO:
        case STATUS_DENIED:
          return props.submitApprovalRequest(
            values[PROCESS_REQUEST_STATUS_FIELD],
            props.arType,
            submitValues
          )

        default:
          throw new SubmissionError({
            [PROCESS_REQUEST_STATUS_FIELD]: 'Error: Invalid status selected',
          })
      }
    },
  })(ProcessApprovalRequestForm)
)
